import React, { useState, useEffect } from 'react';
import { ui } from './ui';
import UploadStep from './UploadStep';
import BlueprintStep from './BlueprintStep';
import EditorStep from './EditorStep';
import { generatePaper, getStatus, errMsg } from './aiPaperApi';
import { defaultBlueprint, blueprintToRequest, blueprintTotal, renumber } from './aiPaperUtils';

const ALLOWED = ['principal', 'admin', 'teacher', 'teacher_admin_level1'];
const STEPS = ['1. Source PDF', '2. Blueprint', '3. Edit & Export'];

const initialConfig = {
  title: '', examType: 'Half-Yearly', session: '2026-2027', class: '8', subject: '',
  duration: '3 Hours', maxMarks: 80, languageMode: 'english', pageFrom: '', pageTo: ''
};

function AIPaperGenerator({ user, onClose, academicYear, initialConfig: preset }) {
  const [step, setStep] = useState(0);
  const [source, setSource] = useState(null);
  const [config, setConfig] = useState({ ...initialConfig, ...(preset || {}), session: academicYear || initialConfig.session });
  const [blueprint, setBlueprint] = useState(defaultBlueprint());
  const [paper, setPaper] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [ai, setAi] = useState(null);
  const [style, setStyle] = useState(null); // { docId, name, profile } from a cloned sample paper

  const allowed = ALLOWED.includes(user?.role);

  useEffect(() => {
    if (allowed) getStatus(user).then(setAi).catch(() => setAi({ aiConfigured: false }));
  }, [allowed, user]);

  if (!allowed) {
    return (
      <div style={ui.overlay}>
        <div style={{ ...ui.panel, maxWidth: 420, textAlign: 'center' }}>
          <h3>Access restricted</h3>
          <p>The AI Paper Generator is available to the Principal, Admin and Teachers only.</p>
          <button style={ui.btnDanger} onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const generate = async () => {
    setError('');
    if (!config.subject.trim()) return setError('Please enter the subject.');
    if (!blueprintTotal(blueprint)) return setError('Add at least one question in the blueprint.');
    setGenerating(true);
    try {
      const res = await generatePaper(user, {
        ...config,
        maxMarks: Number(config.maxMarks) || 0,
        pageFrom: Number(config.pageFrom) || 0,
        pageTo: Number(config.pageTo) || 0,
        sourceDocId: source._id,
        styleSourceId: style?.docId || undefined,
        sections: blueprintToRequest(blueprint)
      });
      if (!res.paper.sections.some((s) => s.questions.length)) {
        setError('The AI could not produce any grounded questions from this text. Try a different page range or PDF.');
      } else {
        setPaper(renumber(res.paper));
        setWarnings(res.warnings || []);
        setStep(2);
      }
    } catch (e) { setError(errMsg(e)); }
    finally { setGenerating(false); }
  };

  const goNext = () => {
    setError('');
    if (step === 0 && !source) return setError('Upload a chapter PDF (or pick a previous one) to continue.');
    if (step === 0) return setStep(1);
    if (step === 1) generate();
  };

  return (
    <div style={ui.overlay}>
      <div style={ui.panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: '#312e81', fontSize: 21, fontWeight: 900 }}>📄 AI Question Paper Generator</h2>
            <div style={{ fontSize: 12, color: '#64748b' }}>Questions are generated only from your uploaded textbook chapter.</div>
          </div>
          <button style={ui.btnDanger} onClick={onClose}>Close</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <button key={s} disabled={i > step || (i === 2 && !paper)} onClick={() => setStep(i)}
              style={{ flex: 1, minWidth: 120, padding: '9px 6px', borderRadius: 8, border: 'none', fontWeight: 800, fontSize: 13,
                cursor: i > step ? 'default' : 'pointer', background: i === step ? '#4f46e5' : i < step ? '#c7d2fe' : '#e5e7eb',
                color: i === step ? '#fff' : '#312e81' }}>{s}</button>
          ))}
        </div>

        {ai && !ai.aiConfigured && (
          <div style={{ ...ui.warnBox, marginBottom: 12 }}>
            ⚠️ No AI key is configured on the server (set GEMINI_API_KEY, OPENAI_API_KEY or ANTHROPIC_API_KEY). Upload works, but generating needs a key.
          </div>
        )}

        {step === 0 && <UploadStep user={user} source={source} onSourceReady={setSource} />}
        {step === 1 && <BlueprintStep user={user} config={config} setConfig={setConfig} blueprint={blueprint} setBlueprint={setBlueprint} totalPages={source?.totalPages} style={style} setStyle={setStyle} />}
        {step === 2 && paper && <EditorStep user={user} paper={paper} setPaper={setPaper} warnings={warnings} />}

        {error && <div style={ui.errorBox}>{error}</div>}

        {step < 2 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
            <button style={ui.btnGhost} disabled={step === 0} onClick={() => setStep(step - 1)}>← Back</button>
            <button style={ui.btnPrimary} disabled={generating} onClick={goNext}>
              {generating ? '⏳ Generating from PDF… (can take 1–2 min)' : step === 0 ? 'Next: Blueprint →' : '✨ Generate Paper'}
            </button>
          </div>
        )}
        {step === 2 && (
          <div style={{ marginTop: 14 }}>
            <button style={ui.btnGhost} onClick={() => setStep(1)}>← Back to blueprint (regenerates all questions)</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIPaperGenerator;
